import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { SecondaryButton } from '../../components/Button'
import { InlinePill } from '../../components/DashboardPrimitives'
import { TextInput } from '../../components/Input'
import { Customer, WithdrawalAccount } from '../../state/Radom'
import RecipientAvatar from './RecipientAvatar'
import {
  RecentChip,
  RecentChipsRow,
  SectionLabel,
  SelectedRecipientCard,
  SelectionLabel,
  SelectionMeta,
  SelectionSubtext,
  focusRing
} from './SendStyles'
import {
  SendDestination,
  customerDisplayName,
  destinationDisplayName,
  destinationKey,
  destinationMode,
  detectOneOffKind,
  normalizeOneOffInput,
  oneOffKindLabel,
  withdrawalAccountSubtitle
} from './sendUtil'

const PickerWrapper = styled.div`
  position: relative;
`

const ResultsPanel = styled.div<{ $stickyFooter?: boolean }>`
  background: var(--surface, #ffffff);
  border: 1px solid var(--border, #e6e9ef);
  border-radius: 16px;
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.12);
  left: 0;
  /* Never taller than the space below the input: the page doesn't grow for
     absolutely-positioned content, so anything past the viewport would be
     unreachable rather than scrollable. */
  max-height: min(380px, 55vh);
  overflow-y: auto;
  position: absolute;
  right: 0;
  /* Keyboard scrollIntoView must not tuck the highlighted row behind the
     sticky create footer (two ~58px rows plus its border and padding). */
  scroll-padding-bottom: ${({ $stickyFooter }) => $stickyFooter ? '128px' : '0'};
  top: calc(100% + 6px);
  z-index: 60;
`

const ResultsGroupLabel = styled.div`
  color: var(--text-muted, #6f7888);
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.03em;
  padding: 12px 12px 4px;
  text-transform: uppercase;
`

const ResultRow = styled.button<{ $active?: boolean }>`
  align-items: center;
  background: ${({ $active }) => $active ? 'var(--surface-hover, #f5f6f7)' : 'transparent'};
  border: 0;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  font-family: var(--font-sans);
  gap: 12px;
  margin: 0 4px;
  padding: 10px 8px;
  text-align: left;
  transition: background-color 0.16s ease;
  width: calc(100% - 8px);

  &:hover {
    background: var(--surface-hover, #f5f6f7);
  }

  ${focusRing}
`

const PanelFootnote = styled.div`
  border-top: 1px solid var(--surface-hover, #eef1f6);
  color: var(--text-soft, #7d8492);
  font-size: 12px;
  line-height: 1.5;
  padding: 10px 12px;
`

const PanelEmptyState = styled.div`
  color: var(--text-muted, #707887);
  font-size: 12.75px;
  line-height: 1.5;
  padding: 16px 12px;
  text-align: center;
`

/**
 * Pinned "save a new destination" section so picking a recipient never means
 * leaving Send. Sticky, so it stays visible while long result lists scroll;
 * the panel's scroll-padding keeps keyboard navigation clear of it.
 */
const CreateFooter = styled.div`
  background: var(--surface, #ffffff);
  border-top: 1px solid var(--surface-hover, #eef1f6);
  bottom: 0;
  padding: 4px 0;
  position: sticky;
`

const CreateRowGlyph = styled.div`
  align-items: center;
  border: 1px dashed var(--border-strong, #c3cad6);
  border-radius: 999px;
  color: var(--text-muted, #5b6472);
  display: flex;
  flex: 0 0 auto;
  font-size: 17px;
  font-weight: 500;
  height: 32px;
  justify-content: center;
  line-height: 1;
  width: 32px;
`

const GroupFootnote = styled.div`
  color: var(--text-faint, #8a919e);
  font-size: 11.5px;
  line-height: 1.45;
  padding: 4px 12px 8px;
`

/** Type filters so a bank account is findable among dozens of wallets. */
const FilterRow = styled.div`
  background: var(--surface, #ffffff);
  border-bottom: 1px solid var(--surface-hover, #eef1f6);
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 12px;
  position: sticky;
  top: 0;
  z-index: 1;
`

const FilterPill = styled.button<{ $active?: boolean }>`
  background: ${({ $active }) => $active ? 'var(--ink, #20242d)' : 'var(--surface-hover, #f4f6fa)'};
  border: 0;
  border-radius: 999px;
  color: ${({ $active }) => $active ? 'var(--surface, #ffffff)' : 'var(--text-muted, #5b6472)'};
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  padding: 7px 12px;
  transition: background-color 0.16s ease, color 0.16s ease;

  ${focusRing}
`

const destinationSubtitle = (destination: SendDestination): string => {
  switch (destination.kind) {
  case 'ownAccount':
    return withdrawalAccountSubtitle(destination.account)
  case 'customer':
    return destination.customer.email?.trim() || 'Saved recipient'
  case 'oneOff':
    return `${oneOffKindLabel(destination.oneOffKind)} · One-off destination`
  }
}

const destinationTitle = (destination: SendDestination): string => {
  switch (destination.kind) {
  case 'ownAccount':
    return destination.account.name?.trim() || 'Withdrawal account'
  case 'customer':
    return customerDisplayName(destination.customer)
  case 'oneOff':
    return destination.value
  }
}

const destinationTagLabel = (destination: SendDestination): string => {
  switch (destination.kind) {
  case 'ownAccount':
    return 'Your account'
  case 'customer':
    return 'Recipient'
  case 'oneOff':
    return oneOffKindLabel(destination.oneOffKind)
  }
}

const optionId = (index: number): string => `send-destination-option-${index}`

/** Which kind of saved destination the in-flow create rows produce. */
export type CreateAccountKind = 'bank' | 'wallet'

const CREATE_ROWS: Array<{ kind: CreateAccountKind, title: string, subtitle: string }> = [
  { kind: 'bank', title: 'New bank account', subtitle: 'Save an account you can withdraw to' },
  { kind: 'wallet', title: 'New wallet address', subtitle: 'Save a crypto address' }
]

/** One keyboard-navigable row in the open panel: a result or a create action. */
type PickerItem =
  | { type: 'destination', destination: SendDestination }
  | { type: 'create', create: CreateAccountKind }

interface IProps {
  accounts: WithdrawalAccount[]
  customers: Customer[]
  isLoading: boolean
  canReadAccounts: boolean
  canReadCustomers: boolean
  /** Gates the in-flow create rows on the WriteWithdrawalAccount permission. */
  canCreateAccounts: boolean
  selected?: SendDestination
  /** Recently paid destinations, rendered as one-tap chips above the search. */
  recent?: SendDestination[]
  onSelect: (destination: SendDestination) => void
  onClear: () => void
  /** Opens the save-a-destination modal without leaving the Send flow. */
  onCreateAccount: (kind: CreateAccountKind) => void
}

type RecipientFilter = 'all' | 'bank' | 'wallet' | 'contact'

const DestinationPicker = (props: IProps): ReactElement => {
  const [search, setSearch] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [filter, setFilter] = useState<RecipientFilter>('all')
  const [activeIndex, setActiveIndex] = useState(0)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)

  // The panel is absolutely positioned, so the page never grows for it: cap
  // its height to the space actually left below the input, else the tail of
  // the list paints past the viewport where it can't be reached.
  useEffect(() => {
    if (!isOpen) return
    const applyMaxHeight = (): void => {
      const panel = panelRef.current
      if (!panel) return
      const top = panel.getBoundingClientRect().top
      const available = Math.max(160, window.innerHeight - top - 16)
      panel.style.maxHeight = `${Math.min(380, available)}px`
    }
    applyMaxHeight()
    window.addEventListener('resize', applyMaxHeight)
    return () => window.removeEventListener('resize', applyMaxHeight)
  }, [isOpen])
  const recentRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const keyboardNav = useRef(false)

  // Close on outside interaction and on Escape so the panel never traps the
  // keyboard. Escape also returns focus to the page rather than the input.
  useEffect(() => {
    if (!isOpen) return

    const onPointerDown = (e: MouseEvent): void => {
      const target = e.target as Node
      // The recent chips sit outside the input wrapper but are part of the
      // picker: closing on their mousedown would swallow the chip's click and
      // force a second tap.
      if (recentRef.current?.contains(target)) return
      if (wrapperRef.current && !wrapperRef.current.contains(target)) {
        setIsOpen(false)
      }
    }
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setIsOpen(false)
        inputRef.current?.blur()
      }
    }

    window.addEventListener('mousedown', onPointerDown)
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('mousedown', onPointerDown)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [isOpen])

  const query = search.trim().toLowerCase()

  const matchedAccounts = useMemo(() => props.accounts.filter(account => {
    if (!query) return true
    const fiat = account.accountDetails.Fiat
    const crypto = account.accountDetails.Crypto
    return [
      account.name,
      crypto?.address,
      crypto?.network,
      fiat?.accountOwnerName,
      fiat?.accountType.iban?.iban,
      fiat?.accountType.us?.accountNumber,
      fiat?.accountType.us?.bankName
    ].some(term => term?.toLowerCase().includes(query))
  }), [props.accounts, query])

  const matchedCustomers = useMemo(() => props.customers.filter(customer => {
    if (!query) return true
    return [
      customer.name,
      customer.companyName,
      customer.email,
      customer.id
    ].some(term => term?.toLowerCase().includes(query))
  }), [props.customers, query])

  const oneOffKind = detectOneOffKind(search)

  // Banks are the needle in a haystack of wallet addresses: list them first,
  // and let the filter pills cut the panel to one recipient type.
  const matchedBanks = useMemo(
    () => matchedAccounts.filter(account => !!account.accountDetails.Fiat),
    [matchedAccounts])
  const matchedWallets = useMemo(
    () => matchedAccounts.filter(account => !!account.accountDetails.Crypto),
    [matchedAccounts])

  const visibleBanks = filter === 'all' || filter === 'bank' ? matchedBanks : []
  const visibleWallets = filter === 'all' || filter === 'wallet' ? matchedWallets : []
  const visibleCustomers = filter === 'all' || filter === 'contact' ? matchedCustomers : []

  // Flat list in rendered order, so ArrowUp/Down walk exactly what's visible.
  const flatResults = useMemo((): SendDestination[] => {
    const list: SendDestination[] = []
    if (oneOffKind) {
      list.push({ kind: 'oneOff', value: normalizeOneOffInput(search), oneOffKind })
    }
    visibleBanks.forEach(account => list.push({ kind: 'ownAccount', account }))
    visibleWallets.forEach(account => list.push({ kind: 'ownAccount', account }))
    visibleCustomers.forEach(customer => list.push({ kind: 'customer', customer }))
    return list
  }, [oneOffKind, search, visibleBanks, visibleWallets, visibleCustomers])

  // The create rows in the footer join the same keyboard list, after the
  // results, exactly as rendered.
  const flatItems = useMemo((): PickerItem[] => {
    const items: PickerItem[] = flatResults.map(destination => ({ type: 'destination', destination }))
    if (props.canCreateAccounts) {
      CREATE_ROWS.forEach(row => items.push({ type: 'create', create: row.kind }))
    }
    return items
  }, [flatResults, props.canCreateAccounts])

  useEffect(() => {
    setActiveIndex(0)
  }, [query, isOpen, filter])

  // A closed panel forgets its filter: reopening always starts from the full
  // list, matching how search resets after a pick.
  useEffect(() => {
    if (!isOpen) setFilter('all')
  }, [isOpen])

  // Keep the keyboard-highlighted row in view; mouse hover must not scroll.
  useEffect(() => {
    if (!keyboardNav.current) return
    keyboardNav.current = false
    document.getElementById(optionId(activeIndex))?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  const hasSavedRecipients = props.accounts.length > 0 || props.customers.length > 0
  const hasResults = flatResults.length > 0

  const selectDestination = (destination: SendDestination): void => {
    props.onSelect(destination)
    setSearch('')
    setIsOpen(false)
  }

  const activateItem = (item: PickerItem): void => {
    if (item.type === 'destination') {
      selectDestination(item.destination)
      return
    }
    props.onCreateAccount(item.create)
    setIsOpen(false)
  }

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (!isOpen) {
        setIsOpen(true)
        return
      }
      keyboardNav.current = true
      setActiveIndex(index => e.key === 'ArrowDown'
        ? Math.min(index + 1, flatItems.length - 1)
        : Math.max(index - 1, 0))
      return
    }

    if (e.key === 'Enter' && isOpen && flatItems[activeIndex]) {
      // Selecting a result must not double as "advance the form".
      e.preventDefault()
      e.stopPropagation()
      activateItem(flatItems[activeIndex])
    }
  }

  const renderRow = (destination: SendDestination, index: number): ReactElement => {
    const title = destination.kind === 'oneOff'
      ? `Send to this ${destination.oneOffKind === 'iban' ? 'IBAN' : 'address'}`
      : destinationTitle(destination)
    const subtitle = destination.kind === 'oneOff'
      ? destination.value
      : destinationSubtitle(destination)

    return <ResultRow
      key={destinationKey(destination)}
      id={optionId(index)}
      role='option'
      aria-selected={index === activeIndex}
      $active={index === activeIndex}
      onMouseEnter={() => setActiveIndex(index)}
      onClick={() => selectDestination(destination)}>
      <RecipientAvatar name={destinationDisplayName(destination)} size={32} />
      <SelectionMeta>
        <SelectionLabel>{title}</SelectionLabel>
        <SelectionSubtext>{subtitle}</SelectionSubtext>
      </SelectionMeta>
      <div style={{ flexGrow: 1 }} />
      <InlinePill>{destinationTagLabel(destination)}</InlinePill>
    </ResultRow>
  }

  const renderCreateRow = (row: typeof CREATE_ROWS[number], index: number): ReactElement => (
    <ResultRow
      key={`create-${row.kind}`}
      id={optionId(index)}
      role='option'
      aria-selected={index === activeIndex}
      $active={index === activeIndex}
      onMouseEnter={() => setActiveIndex(index)}
      onClick={() => activateItem({ type: 'create', create: row.kind })}>
      <CreateRowGlyph aria-hidden='true'>+</CreateRowGlyph>
      <SelectionMeta>
        <SelectionLabel>{row.title}</SelectionLabel>
        <SelectionSubtext>{row.subtitle}</SelectionSubtext>
      </SelectionMeta>
    </ResultRow>
  )

  if (props.selected) {
    const destination = props.selected
    return <SelectedRecipientCard>
      <div style={{ alignItems: 'center', display: 'flex', gap: 12, minWidth: 0 }}>
        <RecipientAvatar name={destinationDisplayName(destination)} size={40} />
        <SelectionMeta>
          <SelectionLabel>{destinationTitle(destination)}</SelectionLabel>
          <SelectionSubtext>{destinationSubtitle(destination)}</SelectionSubtext>
          <SelectionSubtext style={{ color: 'var(--text-faint, #8a919e)', fontSize: 11.75 }}>
            {destinationMode(destination) === 'withdrawal'
              ? 'Your own account, booked as a withdrawal'
              : 'Someone you’re paying, booked as a payout'}
          </SelectionSubtext>
        </SelectionMeta>
      </div>
      <div style={{ alignItems: 'center', display: 'flex', flexShrink: 0, gap: 8 }}>
        <InlinePill>
          Books as: {destinationMode(destination) === 'withdrawal' ? 'Withdrawal' : 'Payout'}
        </InlinePill>
        <SecondaryButton
          style={{ minHeight: 34, padding: '0 12px' }}
          onClick={() => {
            props.onClear()
            setSearch('')
            setIsOpen(false)
          }}>
          Change
        </SecondaryButton>
      </div>
    </SelectedRecipientCard>
  }

  const recent = props.recent ?? []
  let rowIndex = -1

  return <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    {recent.length > 0 && <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <SectionLabel>Recent</SectionLabel>
      <RecentChipsRow ref={recentRef}>
        {recent.map(destination => {
          const name = destinationDisplayName(destination)
          return <RecentChip
            key={destinationKey(destination)}
            type='button'
            title={name}
            aria-label={`Send to ${name} again`}
            onMouseDown={e => {
              // Select on pointer-down: the input's focus/close handling runs
              // between mousedown and click and can swallow the click.
              e.preventDefault()
              selectDestination(destination)
            }}
            onClick={e => e.preventDefault()}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                selectDestination(destination)
              }
            }}>
            <RecipientAvatar name={name} size={26} />
            <span>{name}</span>
          </RecentChip>
        })}
      </RecentChipsRow>
    </div>}

    <PickerWrapper ref={wrapperRef}>
      <TextInput
        ref={inputRef}
        value={search}
        autoComplete='off'
        spellCheck={false}
        role='combobox'
        aria-expanded={isOpen}
        aria-controls='send-destination-results'
        aria-activedescendant={isOpen && flatItems[activeIndex] ? optionId(activeIndex) : undefined}
        placeholder='Search saved recipients, or paste a wallet address or IBAN'
        onFocus={() => setIsOpen(true)}
        onKeyDown={onInputKeyDown}
        onChange={e => {
          setSearch(e.target.value)
          setIsOpen(true)
        }} />
      {isOpen && <ResultsPanel
        ref={panelRef}
        $stickyFooter={props.canCreateAccounts}
        id='send-destination-results'
        role='listbox'
        aria-label='Send destinations'>
        {hasSavedRecipients && !props.isLoading && <FilterRow role='group' aria-label='Recipient type filters'>
          {([
            ['all', `All ${matchedBanks.length + matchedWallets.length + matchedCustomers.length}`],
            ['bank', `Bank accounts ${matchedBanks.length}`],
            ['wallet', `Wallets ${matchedWallets.length}`],
            ['contact', `Contacts ${matchedCustomers.length}`]
          ] as Array<[RecipientFilter, string]>).map(([value, label]) =>
            <FilterPill
              key={value}
              type='button'
              $active={filter === value}
              aria-pressed={filter === value}
              onMouseDown={e => e.preventDefault()}
              onClick={() => setFilter(value)}>
              {label}
            </FilterPill>)}
        </FilterRow>}

        {props.isLoading && <PanelEmptyState>Loading recipients…</PanelEmptyState>}

        {!props.isLoading && !hasResults && <PanelEmptyState>
          {query
            ? 'No recipients match. Paste a full wallet address or IBAN to send one-off.'
            : filter !== 'all'
              ? 'Nothing saved under this type yet.'
              : hasSavedRecipients
                ? 'Keep typing to filter your recipients.'
                : props.canCreateAccounts
                  ? 'No saved recipients yet. Save a bank account or wallet address below, or paste a wallet address or IBAN here.'
                  : 'No saved recipients yet. Paste a wallet address or IBAN here to send one-off.'}
        </PanelEmptyState>}

        {oneOffKind && <>
          <ResultsGroupLabel>One-off destination</ResultsGroupLabel>
          {renderRow(flatResults[0], (rowIndex += 1))}
        </>}

        {visibleBanks.length > 0 && <>
          <ResultsGroupLabel>Bank accounts</ResultsGroupLabel>
          {visibleBanks.map(account => renderRow({ kind: 'ownAccount', account }, (rowIndex += 1)))}
        </>}

        {visibleWallets.length > 0 && <>
          <ResultsGroupLabel>Wallet addresses</ResultsGroupLabel>
          {visibleWallets.map(account => renderRow({ kind: 'ownAccount', account }, (rowIndex += 1)))}
        </>}

        {visibleCustomers.length > 0 && <>
          <ResultsGroupLabel>Contacts</ResultsGroupLabel>
          {visibleCustomers.map(customer => renderRow({ kind: 'customer', customer }, (rowIndex += 1)))}
          <GroupFootnote>
            Contacts can be customers, contractors, or employees: anyone you pay.
          </GroupFootnote>
        </>}

        {hasResults && <div style={{ height: 4 }} />}

        {(!props.canReadAccounts || !props.canReadCustomers) && <PanelFootnote>
          {!props.canReadAccounts && 'Your role can’t list saved withdrawal accounts. '}
          {!props.canReadCustomers && 'Your role can’t list recipients. '}
          Paste a full wallet address to send one-off.
        </PanelFootnote>}

        {props.canCreateAccounts && <CreateFooter>
          {CREATE_ROWS.map(row => renderCreateRow(row, (rowIndex += 1)))}
        </CreateFooter>}
      </ResultsPanel>}
    </PickerWrapper>
  </div>
}

export default DestinationPicker
